import { useCallback, useEffect, useState } from "react";
import { Loader2, Database, AlertTriangle, CheckCircle2, FileSearch, Eraser } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api, formatErr, usePoll } from "@/lib/api";
import { toast } from "sonner";

const PLACEHOLDER = /\[(?!\[)[^[\]\n]{2,80}\](?!\(|\])/g;

export function prepPreview(md) {
  return (md || "")
    .replace(/^>\s*Sources:\s*(.+)$/gm, (_, s) => `\`SRC:${s.trim()}\``)
    .replace(/\[\[(\w+)\]\]/g, (_, k) => `\`CHOOSE:${k}\``);
}

export function SourceCode({ children, labels }) {
  const t = String(children);
  if (t.startsWith("SRC:")) return (
    <span className="not-prose flex flex-wrap gap-1.5 mt-1" data-testid="proposal-source-label">
      {t.slice(4).split("·").map((s) => <span key={s} className="text-[11px] font-medium bg-emerald-50 text-emerald-800 border border-emerald-200 rounded-full px-2 py-0.5">{s.trim()}</span>)}
    </span>);
  if (t.startsWith("CHOOSE:")) return <span className="bg-amber-100 text-amber-900 border border-amber-300 rounded px-1.5 py-0.5 text-sm font-medium" data-testid={`proposal-conflict-token-${t.slice(7)}`}>Pick {labels[t.slice(7)] || t.slice(7)} →</span>;
  return <code>{children}</code>;
}

function Field({ f, onPick, busy }) {
  if (!f.conflict) return (
    <div className="py-2 border-b border-slate-100 last:border-0" data-testid={`source-field-${f.key}`}>
      <div className="text-xs text-slate-500">{f.label}</div>
      <div className="text-sm text-slate-900 font-medium break-words">{f.value}</div>
      <div className="text-[11px] text-emerald-700 mt-0.5">{(f.options.find((o) => o.value === f.value)?.sources || ["Your choice"]).join(" · ")}</div>
    </div>);
  return (
    <div className="py-3 px-3 -mx-3 bg-amber-50 border-y border-amber-200" data-testid={`source-conflict-${f.key}`}>
      <div className="text-xs font-semibold text-amber-900 flex items-center gap-1"><AlertTriangle className="h-3.5 w-3.5" />{f.label}: sources disagree</div>
      <div className="grid grid-cols-1 gap-2 mt-2">
        {f.options.map((o, i) => (
          <button key={o.value} disabled={busy} onClick={() => onPick(f.key, o.value)} className="text-left bg-white border border-amber-200 hover:border-emerald-500 rounded-md p-2 transition-colors" data-testid={`source-pick-${f.key}-${i}`}>
            <div className="text-sm font-medium text-slate-900 break-words">{o.value}</div>
            <div className="text-[11px] text-slate-500">{o.sources.join(" · ")}</div>
          </button>))}
      </div>
    </div>);
}

export function SourcesPanel({ proposalId, content, onContent, onLabels, disabled }) {
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(async () => {
    const { data } = await api.get(`/proposals/${proposalId}/sources`);
    setS(data);
    onLabels?.(Object.fromEntries(data.fields.map((f) => [f.key, f.label])));
  }, [proposalId]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { load().catch(() => {}); }, [load]);
  usePoll(load, !!s?.scanning, 5000);

  const pick = async (key, value) => {
    setBusy(true);
    try { const { data } = await api.post(`/proposals/${proposalId}/sources/choose`, { key, value, content }); onContent(data.content); await load(); toast.success("Value applied to the proposal"); }
    catch (e) { toast.error(formatErr(e)); } finally { setBusy(false); }
  };
  const strip = async () => {
    setBusy(true);
    try { const { data } = await api.post(`/proposals/${proposalId}/sources/strip`, { content }); onContent(data.content); toast.success("Source labels removed"); }
    catch (e) { toast.error(formatErr(e)); } finally { setBusy(false); }
  };
  if (!s) return null;
  const holes = (content || "").match(PLACEHOLDER)?.length || 0;
  const labels = (content || "").match(/^>\s*Sources:/gm)?.length || 0;
  const tokens = new Set((content || "").match(/\[\[\w+\]\]/g) || []).size;
  const checks = [
    { ok: !s.scanning, text: s.scanning ? `Reading vault: ${s.vault.read} of ${s.vault.total} documents` : `${s.vault.read} vault documents read`, id: "vault" },
    { ok: !s.conflicts && !tokens, text: s.conflicts || tokens ? `${Math.max(s.conflicts, tokens)} value${Math.max(s.conflicts, tokens) > 1 ? "s" : ""} to pick below` : "No conflicting values", id: "conflicts" },
    { ok: content && !holes, text: !content ? "Generate the proposal" : holes ? `${holes} [placeholder]${holes > 1 ? "s" : ""} left to fill in Edit` : "No placeholders left", id: "placeholders" },
  ];
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-6" data-testid="proposal-sources-panel">
      <div className="flex items-center gap-2"><Database className="h-5 w-5 text-emerald-600" /><h3 className="font-display text-lg font-bold text-slate-900">Pulled from your account</h3></div>
      <p className="text-xs text-slate-500 mt-1">Vault documents, Compliance Blueprint, Property Portfolio, past proposals and Funding Engine.</p>
      <div className="mt-4 space-y-1.5" data-testid="submission-readiness">
        <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">Ready for submission</div>
        {checks.map((c) => (
          <div key={c.id} className={`text-sm flex items-center gap-2 ${c.ok ? "text-emerald-800" : "text-amber-800"}`} data-testid={`readiness-${c.id}`}>
            {c.id === "vault" && !c.ok ? <Loader2 className="h-4 w-4 animate-spin" /> : c.ok ? <CheckCircle2 className="h-4 w-4" /> : <AlertTriangle className="h-4 w-4" />}{c.text}
          </div>))}
        {labels > 0 && <p className="text-[11px] text-slate-500 pt-1">Green source labels are for your review only and are left out of the PDF and Word downloads automatically.</p>}
      </div>
      <div className="mt-4 max-h-[420px] overflow-y-auto pr-1" data-testid="source-fields-list">
        {s.fields.filter((f) => f.conflict).map((f) => <Field key={f.key} f={f} onPick={pick} busy={busy || disabled} />)}
        {s.fields.filter((f) => !f.conflict).map((f) => <Field key={f.key} f={f} />)}
        {!s.fields.length && <p className="text-sm text-slate-400 flex items-center gap-2"><FileSearch className="h-4 w-4" />No details found yet. Upload documents to the vault or add a property.</p>}
        {s.highlights.length > 0 && <p className="text-xs text-slate-500 mt-3" data-testid="source-highlights-count">+ {s.highlights.length} more facts (budgets, code requirements, past wording) used when writing.</p>}
      </div>
      {labels > 0 && <Button variant="outline" size="sm" className="w-full mt-4" onClick={strip} disabled={busy || disabled} data-testid="strip-source-labels-button"><Eraser className="h-4 w-4 mr-2" />Remove source labels from draft</Button>}
    </div>
  );
}
